import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import styles from "../WidgetAnalytics.module.css";
import {
  FiUsers, FiMonitor, FiMessageCircle, FiSmartphone,
  FiActivity, FiGlobe, FiCalendar,
} from "react-icons/fi";
import { NODE_HOST_URL_API3_v1 } from "../../../Dashboard/api/Api";

const LIVE_API_URL = `${NODE_HOST_URL_API3_v1}/admin/widget_live`;

const getAuth = () => {
  const d =
    JSON.parse(localStorage.getItem("login")) ||
    JSON.parse(localStorage.getItem("admin")) ||
    JSON.parse(localStorage.getItem("emp"))   ||
    JSON.parse(localStorage.getItem("reseller"));
  return { user_id: d?.user?.userId, token: d?.token };
};

const timeAgo = (iso) => {
  const secs = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (secs < 60) return `${secs}s ago`;
  return `${Math.floor(secs / 60)}m ago`;
};

const EVENT_MAP = {
  page_view:      { label: "Page View",      color: "#4f46e5", bg: "#eef2ff" },
  widget_open:    { label: "Widget Open",    color: "#d97706", bg: "#fef9c3" },
  whatsapp_click: { label: "WhatsApp Click", color: "#059669", bg: "#f0fdf4" },
};

const SUMMARY_META = [
  { key: "live_visitors",      label: "LIVE VISITORS",      Icon: FiUsers,         color: "#059669", bg: "#f0fdf4", pulse: true },
  { key: "active_widgets",     label: "ACTIVE WIDGETS",     Icon: FiMonitor,       color: "#6366f1", bg: "#f1f5f9" },
  { key: "live_widgets_today", label: "WIDGETS TODAY",      Icon: FiCalendar,      color: "#7c3aed", bg: "#f5f3ff" },
  { key: "widget_opens",       label: "WIDGET OPENS",       Icon: FiMessageCircle, color: "#d97706", bg: "#fef9c3" },
  { key: "whatsapp_clicks",    label: "WHATSAPP CLICKS",    Icon: FiSmartphone,    color: "#0ea5e9", bg: "#f0f9ff" },
];

/* ── Skeleton ─────────────────────────────────────────────────────────────── */

const SkeletonLiveView = () => (
  <>
    <div className="row mb-4">
      {[0, 1, 2, 3, 4].map((i) => (
        <div className="col-6 col-md-3 mb-3" key={i}>
          <div className={styles.skeletonStatCard}>
            <div className={styles.skeletonStatTop}>
              <div className={styles.skeletonDotLabel}>
                <div className={`${styles.skeleton} ${styles.skeletonDot}`} />
                <div className={styles.skeleton} style={{ height: 9, width: "70%" }} />
              </div>
            </div>
            <div className={styles.skeleton} style={{ height: 22, width: "55%", marginBottom: 8 }} />
            <div className={styles.skeleton} style={{ height: 9, width: "80%" }} />
          </div>
        </div>
      ))}
    </div>

    <div className="row mb-4">
      {[0, 1].map((i) => (
        <div className="col-md-6 mb-4 mb-md-0" key={i}>
          <div className={styles.skeletonSectionCard}>
            <div className={styles.skeletonSectionHeader}>
              <div className={styles.skeleton} style={{ height: 13, width: "45%", marginBottom: 6 }} />
              <div className={styles.skeleton} style={{ height: 10, width: "70%" }} />
            </div>
            {[0, 1, 2, 3, 4].map((j) => (
              <div key={j} className={styles.skeletonTableRow} style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                  <div className={styles.skeleton} style={{ height: 10, width: 18, borderRadius: 4 }} />
                  <div className={styles.skeleton} style={{ height: 11, flex: 1 }} />
                </div>
                <div className={styles.skeleton} style={{ height: 6, width: 72, borderRadius: 4 }} />
                <div className={styles.skeleton} style={{ height: 11, width: 24 }} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <div className={styles.skeletonSectionCard}>
      <div className={styles.skeletonSectionHeader}>
        <div className={styles.skeleton} style={{ height: 13, width: "35%", marginBottom: 6 }} />
        <div className={styles.skeleton} style={{ height: 10, width: "55%" }} />
      </div>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className={styles.skeletonTableRow}>
          <div className={styles.skeleton} style={{ height: 22, width: 90, borderRadius: 6, flexShrink: 0 }} />
          <div className={styles.skeleton} style={{ height: 20, width: 74, borderRadius: 20, flexShrink: 0 }} />
          <div className={styles.skeleton} style={{ height: 11, flex: 1 }} />
          <div className={styles.skeleton} style={{ height: 20, width: 54, borderRadius: 20, flexShrink: 0 }} />
        </div>
      ))}
    </div>
  </>
);

/* ── Main component ───────────────────────────────────────────────────────── */

const LiveWidgetView = () => {
  const [summary,  setSummary]  = useState({ live_visitors: 0, active_widgets: 0, live_widgets_today: 0, widget_opens: 0, whatsapp_clicks: 0 });
  const [pages,    setPages]    = useState([]);
  const [widgets,  setWidgets]  = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [apiError,  setApiError]  = useState(null);

  // Full initial fetch — all 4 endpoints, each independent (one failure won't block the rest)
  const fetchAll = useCallback(async () => {
    setApiError(null);
    const { user_id, token } = getAuth();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [sumRes, pagesRes, widgetsRes, activityRes] = await Promise.allSettled([
        axios.post(LIVE_API_URL, { user_id, method: "summary"  }, { headers }),
        axios.post(LIVE_API_URL, { user_id, method: "pages"    }, { headers }),
        axios.post(LIVE_API_URL, { user_id, method: "widgets"  }, { headers }),
        axios.post(LIVE_API_URL, { user_id, method: "activity" }, { headers }),
      ]);

      if (sumRes.status      === "fulfilled" && sumRes.value.data.success)      setSummary(sumRes.value.data.data.summary);
      if (pagesRes.status    === "fulfilled" && pagesRes.value.data.success)    setPages(pagesRes.value.data.data);
      if (widgetsRes.status  === "fulfilled" && widgetsRes.value.data.success)  setWidgets(widgetsRes.value.data.data);
      if (activityRes.status === "fulfilled" && activityRes.value.data.success) setActivity(activityRes.value.data.data);

      if (sumRes.status === "rejected") setApiError(sumRes.reason?.response?.data?.message || sumRes.reason?.message || "Failed to load summary");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) return <SkeletonLiveView />;

  if (apiError) return (
    <div style={{
      background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 12,
      padding: "20px 24px", color: "#e11d48", fontSize: 13, fontWeight: 500,
      fontFamily: "'Inter', sans-serif", display: "flex", alignItems: "center", gap: 10,
    }}>
      <span style={{ fontSize: 18 }}>⚠</span>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Failed to load live data</div>
        <div style={{ color: "#f43f5e", fontSize: 12 }}>{apiError}</div>
      </div>
    </div>
  );

  const maxPage   = Math.max(...pages.map((p) => p.live_visitors), 1);
  const maxWidget = Math.max(...widgets.map((w) => w.live_visitors), 1);

  return (
    <div>

      {/* ── Summary cards ── */}
      <div className="row mb-4">
        {SUMMARY_META.map(({ key, label, Icon, color, bg, pulse }) => (
          <div className="col-6 col-md mb-3" key={key}>
            <div className={styles.statCard}>
              <div className={styles.statDotLabel}>
                <span className={styles.statIconWrap} style={{ background: bg, color }}>
                  <Icon size={13} />
                </span>
                <span className={styles.statLabel}>{label}</span>
                {pulse && <span className={styles.livePulseDot} />}
              </div>
              <div className={styles.statValue}>{(summary[key] ?? 0).toLocaleString()}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Live Pages + Active Widgets ── */}
      <div className="row mb-4">

        <div className="col-md-6 mb-4 mb-md-0">
          <div className={styles.sectionCard}>
            <div className={styles.sectionCardHeader}>
              <div>
                <div className={styles.sectionTitle}>Live Pages</div>
                <div className={styles.sectionSubtitle}>Pages with active visitors right now</div>
              </div>
            </div>
            <div className={styles.lwListBody}>
              {pages.length === 0 ? (
                <div className={styles.tableEmptyCell}>No active pages right now</div>
              ) : pages.map((p, i) => (
                <div key={i} className={`${styles.lwLiveRow} ${i === 0 ? styles.lwLiveRowTop : ""}`}>
                  <span className={styles.lwRank}>#{i + 1}</span>
                  <div className={styles.lwPageUrl}>
                    <FiGlobe size={11} className={styles.lwRowIcon} />
                    <a
                      href={p.page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.lwPageText}
                      title={p.page_url}
                    >{p.page_url}</a>
                  </div>
                  <div className={styles.lwBarCell}>
                    <div className={styles.lwBarTrack}>
                      <div className={styles.lwBarFill} style={{ width: `${Math.round((p.live_visitors / maxPage) * 100)}%` }} />
                    </div>
                    <span className={styles.lwBarCount}>{p.live_visitors}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className={styles.sectionCard}>
            <div className={styles.sectionCardHeader}>
              <div>
                <div className={styles.sectionTitle}>Active Widgets</div>
                <div className={styles.sectionSubtitle}>Widgets with live visitors now</div>
              </div>
            </div>
            <div className={styles.lwListBody}>
              {widgets.length === 0 ? (
                <div className={styles.tableEmptyCell}>No active widgets right now</div>
              ) : widgets.map((w, i) => (
                <div key={i} className={`${styles.lwLiveRow} ${i === 0 ? styles.lwLiveRowTop : ""}`}>
                  <span className={styles.lwRank}>#{i + 1}</span>
                  <div className={styles.lwPageUrl}>
                    <FiMonitor size={11} className={styles.lwRowIcon} />
                    <span className={styles.lwPageText} title={w.temp_type}>{w.temp_type}</span>
                  </div>
                  <div className={styles.lwBarCell}>
                    <div className={styles.lwBarTrack}>
                      <div className={styles.lwBarFill} style={{ width: `${Math.round((w.live_visitors / maxWidget) * 100)}%` }} />
                    </div>
                    <span className={styles.lwBarCount}>{w.live_visitors}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* ── Activity feed ── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionCardHeader}>
          <div>
            <div className={styles.sectionTitle}>Live Activity Feed</div>
            <div className={styles.sectionSubtitle}>Real-time events as they happen</div>
          </div>
          <div className={styles.lwActivityBadge}>
            <FiActivity size={11} />
            {activity.length} events
          </div>
        </div>

        <div className={styles.lwFeedList}>
          <div className={styles.lwFeedHeader}>
            <span className={styles.lwFeedHeaderEvent}>Event</span>
            <span className={styles.lwFeedHeaderVisitor}>Visitor ID</span>
            <span className={styles.lwFeedHeaderWidget}>Widget Type</span>
            <span className={styles.lwFeedHeaderUrl}>URL</span>
            <span className={styles.lwFeedHeaderTime}>Time</span>
          </div>
          {activity.length === 0 ? (
            <div className={styles.tableEmptyCell}>Waiting for activity…</div>
          ) : activity.map((a, i) => {
            const ev = EVENT_MAP[a.event] || { label: a.event, color: "#94a3b8", bg: "#f8fafc" };
            return (
              <div key={i} className={styles.lwFeedRow}>
                <span className={styles.lwEventBadge} style={{ color: ev.color, background: ev.bg }}>
                  {ev.label}
                </span>
                <span className={styles.lwMonoChip}>
                  {a.visitor_id ? String(a.visitor_id).slice(-8) : "–"}
                </span>
                <span className={styles.lwWidgetChip} title={a.temp_type}>
                  {a.temp_type || "–"}
                </span>
                <span className={styles.lwFeedPageUrl} title={a.page_url}>
                  {a.page_url || "–"}
                </span>
                <span className={styles.lwTimeChip}>
                  {a.created_at ? timeAgo(a.created_at) : "–"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default LiveWidgetView;
